import { z } from 'zod';
import { RouteViolationError, ValidationError } from './errors.js';
import { applySizeUpcharges, getDtgTierPrice, round2, } from './helpers.js';
import { DEFAULT_PRICING_CONFIG_BODY } from './pricing-config/defaults.js';
const SizesSchema = z
    .object({
    sxl: z.number().int().min(0),
    xxl: z.number().int().min(0),
    xxxl: z.number().int().min(0),
    xxxxl: z.number().int().min(0).default(0),
    xxxxxl: z.number().int().min(0).default(0),
})
    .refine((s) => s.sxl + s.xxl + s.xxxl + s.xxxxl + s.xxxxxl > 0, {
    message: 'Total quantity across sizes must be greater than zero.',
});
const GarmentSchema = z.discriminatedUnion('source', [
    z.object({
        source: z.literal('we_provide'),
        blankCostEach: z.number().min(0),
    }),
    z.object({
        source: z.literal('customer_provides'),
    }),
]);
export const QuoteDtgInputSchema = z.object({
    sizes: SizesSchema,
    color: z.enum(['light', 'dark']),
    sides: z.enum(['single', 'double']),
    garment: GarmentSchema,
});
export const QuoteDtgResultSchema = z
    .object({
    totalQty: z.number().int(),
    tierBasePrice: z.number(),
    blankCostPerUnit: z.number(),
    sellPerUnit: z.number(),
    internalPrintCostPerUnit: z.number(),
    costPerUnit: z.number(),
    sizeUpchargeTotal: z.number(),
    orderCost: z.number(),
    orderTotal: z.number(),
    profit: z.number(),
    marginPct: z.number(),
    softRouteWarning: z.string().nullable(),
})
    .meta({
    id: 'QuoteDtgResult',
    description: 'DTG quote output. `softRouteWarning` is a human-readable suggestion at qty >= 24, null otherwise. Hard cap at qty >= 48 throws RouteViolationError instead.',
});
/**
 * v16 calcDTG: fixed tier pricing (no margin math). Per-side internal cost is doubled
 * for `double`. Size upcharges are ADDED to orderTotal and orderCost at the order
 * level (not amortized per-unit) — v16 parity; the upcharge reflects extra ink/area
 * work, treated as pass-through so it nets to zero profit.
 *
 * Routing rules:
 *   - qty >= 48 → RouteViolationError (suggest screen_print).
 *   - qty 24-47 → compute normally but set `softRouteWarning`.
 */
export function quoteDtg(rawInput, config) {
    const parsed = QuoteDtgInputSchema.safeParse(rawInput);
    if (!parsed.success) {
        throw new ValidationError(parsed.error.issues);
    }
    const input = parsed.data;
    const cfg = config ?? DEFAULT_PRICING_CONFIG_BODY;
    const dtg = cfg.dtg;
    const sizeUpcharge = cfg.universal.sizeUpcharge;
    const sizeAgg = applySizeUpcharges(input.sizes, sizeUpcharge);
    const totalQty = sizeAgg.totalQty;
    if (totalQty >= dtg.hardMaxQty) {
        throw new RouteViolationError('dtg', `DTG cannot quote ${totalQty} pcs (hard cap ${dtg.hardMaxQty}).`, 'screen_print');
    }
    const blankCostEach = input.garment.source === 'we_provide' ? input.garment.blankCostEach : 0;
    const tierBase = getDtgTierPrice(totalQty, input.color, input.sides, dtg.priceTiers);
    const sellPerUnit = tierBase + blankCostEach;
    const perSideInternal = dtg.internalCost[input.color];
    const sidesMultiplier = input.sides === 'double' ? 2 : 1;
    const internalPrintCost = perSideInternal * sidesMultiplier;
    const costPerUnit = internalPrintCost + blankCostEach;
    const orderTotal = sellPerUnit * totalQty + sizeAgg.totalUpcharge;
    const orderCost = costPerUnit * totalQty + sizeAgg.totalUpcharge;
    const profit = orderTotal - orderCost;
    // v16 margin is print-only: (tierBase - internalPrintCost) / tierBase.
    const marginPct = tierBase > 0 ? ((tierBase - internalPrintCost) / tierBase) * 100 : 0;
    const softRouteWarning = totalQty >= dtg.softMaxQty
        ? `DTG qty ${totalQty} exceeds the ${dtg.softMaxQty - 1}-pc soft max. Consider DTF for full-color small runs.`
        : null;
    return {
        totalQty,
        tierBasePrice: tierBase,
        blankCostPerUnit: blankCostEach,
        sellPerUnit: round2(sellPerUnit),
        internalPrintCostPerUnit: round2(internalPrintCost),
        costPerUnit: round2(costPerUnit),
        sizeUpchargeTotal: sizeAgg.totalUpcharge,
        orderCost: round2(orderCost),
        orderTotal: round2(orderTotal),
        profit: round2(profit),
        marginPct: round2(marginPct),
        softRouteWarning,
    };
}
//# sourceMappingURL=quote-dtg.js.map